import "./MontoBar.scss";
import {
	AmountMode,
	DEFAULT_RET_ISR_PCT,
	DEFAULT_RET_IVA_PCT,
	InvoiceTaxConfig,
	computeBreakdownFor,
	isPersonaMoral,
	money,
	validateTaxConfig,
} from "@services/invoiceTaxes";

// Barra del monto de una factura (tarjeta de solicitud y "Nueva solicitud").
// Apagada: persona física, IVA 16 % incluido. "Ajustar retenciones" encendido:
// se despliegan las retenciones de persona moral (editables) y el desglose.
export function MontoBar({
	amountInputProps,
	amount,
	mode,
	setMode,
	rfc,
	value,
	onChange,
}: {
	amountInputProps: React.InputHTMLAttributes<HTMLInputElement>;
	amount: number;
	mode: AmountMode;
	setMode: (m: AmountMode) => void;
	rfc: string;
	value: InvoiceTaxConfig;
	onChange: (cfg: InvoiceTaxConfig) => void;
}) {
	const error = validateTaxConfig(value);
	const b = !error ? computeBreakdownFor(mode, amount, value) : null;
	const centsOff = b && mode === "total" ? Math.round((b.total - amount) * 100) : 0;
	const setPct = (field: keyof InvoiceTaxConfig) => (e: React.ChangeEvent<HTMLInputElement>) =>
		onChange({ ...value, [field]: e.target.value === "" ? NaN : parseFloat(e.target.value) });
	const toggle = (on: boolean) => {
		onChange({
			...value,
			withholdings: on,
			retIvaPct: isNaN(value.retIvaPct) ? DEFAULT_RET_IVA_PCT : value.retIvaPct,
			retIsrPct: isNaN(value.retIsrPct) ? DEFAULT_RET_ISR_PCT : value.retIsrPct,
		});
		if (!on) setMode("total");
	};

	return (
		<div className="mb">
			<div className="mb-row">
				<label className="mb-amount">
					<span>{mode === "total" ? "Total cobrado" : "Subtotal (antes de impuestos)"}</span>
					<div className="mb-input">
						<em>$</em>
						<input type="number" step="0.01" min={0} max={300000} placeholder="0.00" {...amountInputProps} />
						<em>MXN</em>
					</div>
				</label>
				<div className="mb-quick">
					{b ? (
						<>
							<span>Subtotal {money(b.subtotal)}</span>
							<span>IVA {money(b.iva)}</span>
							{value.withholdings && <span>Retenciones −{money(b.retIva + b.retIsr)}</span>}
							<b>Total {money(b.total)}</b>
						</>
					) : (
						error && <span className="mb-muted">{error}</span>
					)}
				</div>
				<label className="mb-switch">
					<input
						type="checkbox"
						role="switch"
						checked={value.withholdings}
						onChange={(e) => toggle(e.target.checked)}
					/>
					<span className="mb-track" aria-hidden="true" />
					Ajustar retenciones
				</label>
			</div>

			{value.withholdings && (
				<div className="mb-panel">
					<div className="mb-rates">
						<label>
							Retención IVA %
							<input type="number" step="0.0001" min={0} max={16} value={isNaN(value.retIvaPct) ? "" : value.retIvaPct} onChange={setPct("retIvaPct")} />
						</label>
						<label>
							Retención ISR %
							<input type="number" step="0.0001" min={0} max={35} value={isNaN(value.retIsrPct) ? "" : value.retIsrPct} onChange={setPct("retIsrPct")} />
						</label>
						<label>
							IVA trasladado %
							<input type="number" step="0.0001" min={0} max={16} value={isNaN(value.ivaPct) ? "" : value.ivaPct} onChange={setPct("ivaPct")} />
						</label>
						<label className="mb-check">
							<input
								type="checkbox"
								checked={mode === "subtotal"}
								onChange={(e) => setMode(e.target.checked ? "subtotal" : "total")}
							/>
							El monto que escribí es el subtotal
						</label>
					</div>
					{!isPersonaMoral(rfc) && (rfc ?? "").trim().length > 0 && (
						<div className="mb-warning">Ojo: el RFC no es de empresa (12 caracteres); revisa que de verdad lleve retenciones.</div>
					)}
					{b && (
						<table className="mb-table">
							<tbody>
								<tr>
									<td>Subtotal</td>
									<td>{money(b.subtotal)}</td>
								</tr>
								<tr>
									<td>IVA {value.ivaPct} %</td>
									<td>{money(b.iva)}</td>
								</tr>
								<tr>
									<td>Retención IVA {value.retIvaPct} %</td>
									<td>−{money(b.retIva)}</td>
								</tr>
								<tr>
									<td>Retención ISR {value.retIsrPct} %</td>
									<td>−{money(b.retIsr)}</td>
								</tr>
								<tr className="mb-total">
									<td>Total</td>
									<td>{money(b.total)}</td>
								</tr>
							</tbody>
						</table>
					)}
				</div>
			)}
			{b && centsOff !== 0 && (
				<div className="mb-warning">
					Por redondeo, el total de la factura queda {Math.abs(centsOff)} centavo{Math.abs(centsOff) === 1 ? "" : "s"}{" "}
					{centsOff > 0 ? "arriba" : "abajo"} de lo cobrado ({money(amount)}).
				</div>
			)}
		</div>
	);
}
